import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/authStore';

// Create axios instance
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Skip auth for login/signup/refresh endpoints
    const skipAuthPaths = [
      '/auth/login',
      '/auth/signup',
      '/auth/verify',
      '/auth/refresh',
      '/auth/forgot-password',
      '/auth/reset-password',
      '/auth/challenge',
    ];
    const shouldSkipAuth = skipAuthPaths.some(path => config.url?.includes(path));

    if (!shouldSkipAuth) {
      const token = useAuthStore.getState().getIdToken();
      if (token) {
        // Cognito User Pools authorizer expects raw token without "Bearer " prefix
        config.headers.Authorization = token;
      }
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

// Response interceptor - handle 401 and token refresh
apiClient.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // Skip if no config or already retried
    if (!originalRequest || (originalRequest as { _retry?: boolean })._retry) {
      return Promise.reject(error);
    }

    // Skip auth refresh for auth endpoints
    const skipRefreshPaths = ['/auth/login', '/auth/signup', '/auth/verify', '/auth/refresh'];
    const shouldSkipRefresh = skipRefreshPaths.some(path => originalRequest.url?.includes(path));

    if (error.response?.status === 401 && !shouldSkipRefresh) {
      if (isRefreshing) {
        // Wait for the token to be refreshed
        return new Promise(resolve => {
          subscribeTokenRefresh((token: string) => {
            originalRequest.headers.Authorization = token;
            resolve(apiClient(originalRequest));
          });
        });
      }

      (originalRequest as { _retry?: boolean })._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = useAuthStore.getState().tokens?.refresh_token;

        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Call refresh endpoint
        const response = await axios.post(
          `${import.meta.env.VITE_API_BASE_URL}/auth/refresh`,
          { refresh_token: refreshToken },
          { headers: { 'Content-Type': 'application/json' } },
        );

        const { access_token, id_token, expires_in } = response.data.data;

        // Update tokens in store
        const currentTokens = useAuthStore.getState().tokens;
        if (currentTokens) {
          useAuthStore.getState().setTokens({
            ...currentTokens,
            access_token,
            id_token,
            expires_in,
          });
        }

        isRefreshing = false;
        onTokenRefreshed(id_token);

        // Retry original request with new token
        originalRequest.headers.Authorization = id_token;
        return apiClient(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        refreshSubscribers = [];

        // Logout on refresh failure
        useAuthStore.getState().logout();

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

// Error types for better error handling
export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// Helper to extract error message from API response
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const data = error.response?.data;
    if (typeof data === 'object' && data !== null && 'error' in data) {
      return (data as { error: string }).error;
    }
    if (error.message) {
      return error.message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

export default apiClient;
